import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GenericTable } from '@/components/Table/Table';
import { TableSort } from '@/types';
import { getNewSortStateTable } from '@/utils';
import {
  generateURLForReportDetails,
  InvestorListProps,
  InvestorReportingTableData,
  mapApiInvestorReportsDataToTableData,
  sortForPastUpdates,
} from '@/pages/InvestorReporting/utils/investorReports';
// eslint-disable-next-line max-len
import {
  columnsForClientsPastUpdates,
  columnsForInvestorsPastUpdates,
} from '@/pages/InvestorReporting/components/InvestorsViews/tables/investorReportingInvestors.util';
import { IUserContext, useUserContext } from '@/context/UserContext';

function InvestorReportingPastUpdates({
  investorReports,
  recordsPerPage,
  selectedPage,
  onPageSelect,
  onSelectRowsChange,
  searchText,
  investorReportTemplates,
}: InvestorListProps) {
  const { auth, impersonatingAccountId }: IUserContext = useUserContext();
  const navigate = useNavigate();
  const [sort, setSort] = useState<TableSort<InvestorReportingTableData>>({
    field: 'action' as keyof InvestorReportingTableData,
    asc: true,
  });
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const clientNameFromQuery = queryParams.get('clientName') || '';

  const sortBy = (fieldName: keyof InvestorReportingTableData) => {
    const newSort = getNewSortStateTable(fieldName, sort);
    setSort(newSort);
  };

  const sortedReports = useMemo(() => {
    return investorReports.filter((report) =>
      report.client?.client_name
        ?.toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [investorReports, searchText]);

  const reportData = mapApiInvestorReportsDataToTableData(
    sortedReports,
    investorReportTemplates
  );

  const columnsForPastUpdates =
    auth?.isClient || (auth?.isAdmin && impersonatingAccountId)
      ? columnsForClientsPastUpdates
      : columnsForInvestorsPastUpdates;

  return (
    <div className='mt-4'>
      <GenericTable
        className='w-full mb-2'
        tableName='Investor Clients'
        data={reportData as InvestorReportingTableData[]}
        columns={columnsForPastUpdates}
        sortBy={sortBy}
        sort={sort}
        sortFunction={sortForPastUpdates(sort)}
        recordsPerPage={recordsPerPage}
        selectedPage={selectedPage}
        onPageSelect={onPageSelect}
        onSelectRowsChange={onSelectRowsChange}
        onRowClick={(row) => {
          const reportId = row.id ? row.id : undefined;
          const url = generateURLForReportDetails(
            clientNameFromQuery,
            row.client_id,
            row.firm_id,
            row.user_id,
            reportId,
            undefined,
            true
          );
          navigate(url);
        }}
      />
    </div>
  );
}

export default InvestorReportingPastUpdates;
